// src/pages/Integrations/GA4.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Stack,
  VStack,
  HStack,
  Spinner,
  Select,
  FormControl,
  FormLabel,
  useToast,
  SimpleGrid,
  Heading,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Badge,
  Button,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import PlatformWidget from "../../components/themeComponents/PlatformWidget";
import { integrateGA4 } from "../../Services/IntegrationService";
import GA4Icon from "../../images/GA4.png";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../Services/axiosInstance";

export default function GA4() {
  const outletContext = useOutletContext() || {};
  const integration = outletContext?.integration;

  const { t } = useTranslation();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [viewData, setViewData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [savedDetails, setSavedDetails] = useState({
    accountId: null,
    accountName: null,
    propertyId: null,
    propertyName: null,
    viewId: null,
    viewName: null,
  });

  useEffect(() => {
    const fetchViewData = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const response = await axiosInstance.get("/ga4/viewdata");
        const data = response?.data || {};
        setViewData(data);

        if (data?.savedGaDetails) {
          setSavedDetails(data.savedGaDetails);

          if (data.savedGaDetails.propertyId) {
            const propertyIdStr = data.savedGaDetails.propertyId.toString();
            const accounts = data?.gaData || [];

            for (const account of accounts) {
              const property =
                account?.propertyList?.find(
                  (p) =>
                    p?.id === propertyIdStr ||
                    p?.id?.endsWith(`/${propertyIdStr}`)
                ) || null;

              if (property) {
                setSelectedAccount(account?.name || account?.id || "");
                setSelectedProperty(property?.id || "");
                break;
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch GA4 view data:", err);
        setFetchError(err);

        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load Google Analytics data.";
      } finally {
        setLoading(false);
      }
    };

    fetchViewData();
  }, [toast]);

  const ga4Integration = () => {
    integrateGA4()
      .then((res) => {
        const url = res?.data;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          toast({
            title: "Error",
            description:
              "Could not start Google authentication. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch((err) => {
        console.error(err);
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to start Google authentication.";
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const hasGaData =
    Array.isArray(viewData?.gaData) && viewData.gaData.length > 0;

  const handleSave = async () => {
    if (!selectedAccount || !selectedProperty) {
      toast({
        title: "Error",
        description: "Please select both account and property",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!Array.isArray(viewData?.gaData)) {
      toast({
        title: "Error",
        description: "No Google Analytics accounts found.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const selectedAccountObj = viewData?.gaData?.find(
        (acc) => acc?.name === selectedAccount || acc?.id === selectedAccount
      );

      const selectedPropertyObj = selectedAccountObj?.propertyList?.find(
        (prop) =>
          prop?.id === selectedProperty || prop?.name === selectedProperty
      );

      if (!selectedAccountObj || !selectedPropertyObj) {
        throw new Error("Selected account or property not found.");
      }

      const numericPropertyId =
        selectedPropertyObj?.id?.split("/")?.pop() || selectedPropertyObj?.id;

      const payload = {
        accountId:
          selectedAccountObj?.name || selectedAccountObj?.id || null,
        accountName:
          selectedAccountObj?.name ||
          selectedAccountObj?.displayName ||
          selectedAccountObj?.id ||
          null,
        propertyId: numericPropertyId,
        propertyName:
          selectedPropertyObj?.name ||
          selectedPropertyObj?.displayName ||
          numericPropertyId ||
          null,
        viewId: null,
        viewName: null,
      };

      await axiosInstance.post(
        "/v1/googleanalytics/savegadetails",
        payload
      );

      setSavedDetails(payload);

      toast({
        title: "Success",
        description: "Google Analytics 4 property connected successfully!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to save GA details:", err);

      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to connect property. Please try again.";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await axiosInstance.get("/v1/googleanalytics/clearlogin");

      setSavedDetails({
        accountId: null,
        accountName: null,
        propertyId: null,
        propertyName: null,
        viewId: null,
        viewName: null,
      });
      setSelectedAccount("");
      setSelectedProperty("");

      toast({
        title: "Success",
        description: "Google Analytics 4 property disconnected successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to disconnect GA4:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to disconnect. Please try again.";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const isCurrentlyConnected = savedDetails?.propertyId != null;

  const selectedAccountObj = viewData?.gaData?.find(
    (acc) => acc?.name === selectedAccount || acc?.id === selectedAccount
  );

  const selectedPropertyObj = selectedAccountObj?.propertyList?.find(
    (p) => p?.id === selectedProperty || p?.name === selectedProperty
  );

  const selectedPropertyNumericId =
    selectedPropertyObj?.id?.split("/")?.pop() || selectedPropertyObj?.id;

  const isSelectionDifferent =
    !isCurrentlyConnected ||
    savedDetails?.propertyId !==
      (selectedProperty ? selectedPropertyNumericId : null);

  const steps = [
    t("ga4.steps.step1"),
    t("ga4.steps.step2"),
    t("ga4.steps.step3"),
  ];

  const showSimpleHeaderState = loading || (!hasGaData && !fetchError);

  return (
    <Box bg="gray.50" minH="100vh" py={10}>
      <VStack
        align="stretch"
        spacing={6}
        maxW="5xl"
        mx="auto"
        px={{ base: 4, md: 0 }}
      >
        {/* Header widget only: icon + title + CTA */}
        <PlatformWidget
          platform={{
            title: t("ga4.title", { entityName: integration?.entityName }),
          }}
          icon={GA4Icon}
          WidgetContent={() => (
            <VStack align="flex-start" spacing={2}>
              <Text fontSize="sm" color="gray.600">
                {t("ga4.description")}
              </Text>

              {showSimpleHeaderState && (
                <PrimaryButton
                  label={t("ga4.signInWithGoogle")}
                  onClick={ga4Integration}
                  isDisabled={loading}
                />
              )}
            </VStack>
          )}
          styles={{ width: "100%" }}
        />

        {/* Content below the widget: state-driven */}
        {loading ? (
          <Box
            bg="white"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.100"
            p={8}
            boxShadow="sm"
          >
            <Box py={8} display="flex" justifyContent="center">
              <Spinner size="lg" thickness="4px" />
            </Box>
          </Box>
        ) : fetchError && !hasGaData ? (
          <Card variant="outline">
            <CardBody>
              <VStack align="flex-start" spacing={3}>
                <PrimaryButton
                  label={t("ga4.signInWithGoogle")}
                  onClick={ga4Integration}
                />
              </VStack>
            </CardBody>
          </Card>
        ) : !hasGaData ? (
          <Card variant="outline">
            <CardBody>
              <VStack align="flex-start" spacing={3}>
                <Text fontSize="sm" color="gray.600">
                  {t("ga4.noAccountsMessage", "No GA4 accounts found for this user.")}
                </Text>
                <PrimaryButton
                  label={t("ga4.signInWithGoogle")}
                  onClick={ga4Integration}
                />
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <>
            {/* Connected info + dropdown form */}
            <Card variant="outline">
              <CardBody as={Stack} spacing={6}>
                <HStack justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" mb={1}>
                      Google Analytics 4 property
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Select the account and property you want to connect.
                    </Text>
                  </Box>
                  {isCurrentlyConnected && (
                    <Badge
                      colorScheme="green"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      <CheckIcon boxSize={3} />
                      <Text fontSize="xs">Connected</Text>
                    </Badge>
                  )}
                </HStack>

                {isCurrentlyConnected && (
                  <Card
                    variant="outline"
                    borderColor="green.100"
                    bg="green.50"
                    size="sm"
                  >
                    <CardHeader pb={2}>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="green.700"
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                      >
                        Currently connected
                      </Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      <SimpleGrid
                        columns={{ base: 1, md: 2 }}
                        spacing={4}
                        fontSize="sm"
                      >
                        {savedDetails?.accountName && (
                          <Box>
                            <Text color="gray.500">Account</Text>
                            <Text fontWeight="medium">
                              {savedDetails?.accountName}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              ID: {savedDetails?.accountId}
                            </Text>
                          </Box>
                        )}
                        <Box>
                          <Text color="gray.500">Property</Text>
                          <Text fontWeight="medium">
                            {savedDetails?.propertyName ||
                              savedDetails?.propertyId}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {savedDetails?.propertyId}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </Card>
                )}

                <Stack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Account</FormLabel>
                    <Select
                      placeholder="Select account"
                      value={selectedAccount}
                      onChange={(e) => {
                        setSelectedAccount(e.target.value);
                        setSelectedProperty("");
                      }}
                      bg="gray.50"
                      _focus={{ borderColor: "blue.400", bg: "white" }}
                    >
                      {viewData?.gaData?.map((account) => (
                        <option
                          key={account?.name || account?.id}
                          value={account?.name || account?.id}
                        >
                          {account?.name ||
                            account?.displayName ||
                            account?.id}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedAccount && (
                    <FormControl>
                      <FormLabel fontSize="sm">Property</FormLabel>
                      <Select
                        placeholder="Select property"
                        value={selectedProperty}
                        onChange={(e) =>
                          setSelectedProperty(e.target.value)
                        }
                        bg="gray.50"
                        _focus={{ borderColor: "blue.400", bg: "white" }}
                      >
                        {selectedAccountObj?.propertyList?.map(
                          (property) => {
                            const numericId =
                              property?.id?.split("/")?.pop() ||
                              property?.id;
                            return (
                              <option
                                key={property?.id}
                                value={property?.id}
                              >
                                {numericId} –{" "}
                                {property?.name ||
                                  property?.displayName ||
                                  "(No name)"}
                              </option>
                            );
                          }
                        )}
                      </Select>
                    </FormControl>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    {isSelectionDifferent && selectedProperty ? (
                      <PrimaryButton
                        label={
                          saving ? "Connecting..." : "Connect property"
                        }
                        onClick={handleSave}
                        isDisabled={saving || !selectedProperty}
                      />
                    ) : isCurrentlyConnected ? (
                      <>
                        <Text
                          color="green.600"
                          fontWeight="medium"
                          fontSize="sm"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <CheckIcon boxSize={3} />
                          Property is connected
                        </Text>
                        <Button
                          variant="outline"
                          colorScheme="red"
                          size="sm"
                          onClick={handleDisconnect}
                          isLoading={disconnecting}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : null}
                  </Box>
                </Stack>
              </CardBody>
            </Card>

            {/* Steps card */}
            <Card variant="outline">
              <CardHeader borderBottomWidth="1px">
                <Heading size="sm">{t("ga4.stepsTitle")}</Heading>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  {steps.map((step, index) => (
                    <HStack key={index} align="flex-start" spacing={4}>
                      <VStack spacing={0}>
                        <Box
                          w={6}
                          h={6}
                          borderRadius="full"
                          bg="blue.50"
                          borderWidth="1px"
                          borderColor="blue.300"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="xs"
                          fontWeight="semibold"
                          color="blue.700"
                        >
                          {index + 1}
                        </Box>
                        {index !== steps.length - 1 && (
                          <Box
                            flex="1"
                            w="1px"
                            bg="gray.200"
                            minH={8}
                            mt={1}
                          />
                        )}
                      </VStack>
                      <Text fontSize="sm" color="gray.700">
                        {step}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          </>
        )}
      </VStack>
    </Box>
  );
}